/* eslint-disable sort-keys */
import { registerClass } from '../SatCoreRegistry';

import accommodationsManager from '../managers/AccommodationsManager';

import { ACCOMMODATION_TOGGLE_KEY } from './AccommodationConstants';

export default class AccommodationsService {
  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  static getAvailableSectionToggleCount = ({ sectionKey } = {}) => {
    const config = this.ACCOMMODATIONS_CONFIG();
    let count = 0;
    for (const toggleKey of Object.keys(config[sectionKey].toggles)) {
      if (config[sectionKey].toggles[toggleKey]) {
        count++;
      }
    }
    return count;
  }

  static getOffAccommodationsForAll = ({
    assignment = null,
    config,
    rosterItems = [],
    sectionKey = 'assessmentSection',
  } = {}) => {
    config = config || this.ACCOMMODATIONS_CONFIG();
    const toggleKeysTurnedOnByConfig = this.getConfigEnabledAccommodationToggleKeys({
      config, sectionKey
    });
    rosterItems = rosterItems.length ? rosterItems : accommodationsManager.currentAccommodationsRosterArray;

    let offAccommodationsForAll = [];

    if (assignment) {
      offAccommodationsForAll = assignment.additionalPropertiesJson?.accommodationIds?.split(',') || [];
      return offAccommodationsForAll;
    } else {
      for (const toggleKey of toggleKeysTurnedOnByConfig) {
        const isAccommodationOn = (rosterItems)?.every((rosterItem) => {
          return !rosterItem.offAccommodations?.includes(
            ACCOMMODATION_TOGGLE_KEY[sectionKey][toggleKey]
          );
        });
        if (!isAccommodationOn) {
          offAccommodationsForAll.push(toggleKey);
        }
      }
      return offAccommodationsForAll;
    }
  }

  static getConfigEnabledAccommodationToggleKeys = ({
    config,
    sectionKey = 'assessmentSection',
  } = {}) => {
    config = config || this.ACCOMMODATIONS_CONFIG();
    const toggleKeys = Object.keys(config[sectionKey].toggles);
    return toggleKeys.filter((toggleKey) => {
      return config[sectionKey].toggles[toggleKey];
    });
  }

  /**
   * Fetch publisher translations and transform to localeId: displayName format
   * Uses cached value if available
   * @returns {Promise<Object>} - Object with localeId as keys and displayName as values
   */
  static fetchTranslationLanguages = async () => {
    return await accommodationsManager.fetchTranslationLanguages();
  }

  /**
   * Fetch locale names from the backend API (with caching)
   * @returns {Promise<Object|null>} Object mapping locale id to name (e.g., { en: 'English' })
   */
  static fetchLocaleNames = async () => {
    return await accommodationsManager.fetchLocaleNames();
  }

  /**
   * Update user locale preference
   * @param {string} userId - User ID to update
   * @param {string} localeId - Locale ID to update (e.g., 'en', 'es')
   * @returns {Promise<Object>} { success: boolean, localeId?: string, message?: string }
   */
  static updateUserLocale = async (userId, localeId) => {
    return await accommodationsManager.updateUserLocale(userId, localeId);
  }

  /**
   * Update the locale the spot Translate tool (ReadSpeaker/TextHelp) translates into
   * @param {string} userId - User ID to update
   * @param {string} spotTranslationLocaleId - Locale ID (e.g., 'es'); empty clears the preference
   * @returns {Promise<Object>} { success: boolean, spotTranslationLocaleId?: string, message?: string }
   */
  static updateUserSpotTranslationLocale = async (userId, spotTranslationLocaleId) => {
    return await accommodationsManager.updateUserSpotTranslationLocale(userId, spotTranslationLocaleId);
  }

  /**
   * Fetch publisher options from the backend API (with caching)
   * @returns {Promise<Object|null>} Object containing publisher options like hasWebReaderId, hasTextHelpId, aiEnabled, etc.
   */
  static fetchPublisherOptions = async () => {
    return await accommodationsManager.fetchPublisherOptions();
  }

  /** can be overridden */
  static ACCOMMODATIONS_CONFIG = () => {
    return {
      assessmentSection: {
        show: true, // overrides all other assessment options if false
        toggles: {
          // toggles marked as true will appear in the order shown here
          basicCalculatorToggle: true,
          // TTS group: Text To Speech, Dictionary, Spot Translation — C2C Translate (content translation)
          // always renders immediately after them. Satellites opt in to c2cTranslationToggle.
          textToSpeechToggle: true,
          dictionaryToggle: false,
          translationToggle: true,
          c2cTranslationToggle: false,
          contentLanguageToggle: true,
          scientificCalculatorToggle: false,
          graphingCalculatorToggle: false,
          spellcheckerToggle: false
        }
      },
      lessonSection: {
        show: true, // overrides all other lesson options if false
        toggles: {
          // toggles marked as true will appear in the order shown here
          basicCalculatorToggle: true,
          // TTS group: Text To Speech, Dictionary, Spot Translation — C2C Translate (content translation)
          // always renders immediately after them. Satellites opt in to c2cTranslationToggle.
          textToSpeechToggle: true,
          dictionaryToggle: true,
          translationToggle: true,
          c2cTranslationToggle: false,
          scientificCalculatorToggle: false,
          graphingCalculatorToggle: false,
          spellcheckerToggle: false
        }
      },
      cellDividerClassName: 'section-divider',
      showTopHeader: true,
      tableHeaderFontSize: 'inherit',
      toggleGroups: [ // support mapping of UI toggles to accommodation IDs (that will be sent to backend)
        // examples
        // ['ASM_BASIC_CALC', ['ASM_BASIC_CALC', 'ASM_GRAPH_CALC', 'ASM_SCI_CALC']],
        // ['LSN_TEXT_SPEECH', ['LSN_DICTIONARY', 'LSN_TRANSLATION']]
        // ['ASM_TEXT_SPEECH', ['ASM_CONTENT_LANG', 'ASM_TRANSLATION']],
        // ['ASM_BASIC_CALC', ['ASM_BASIC_CALC', 'LSN_BASIC_CALC']]
      ],
      treatAllSectionsAsOne: false
    };
  }
}

registerClass('AccommodationsService', AccommodationsService);
